import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { supabase, addVocabularyTerm } from "../lib/supabase";
import { VoiceButton } from "../components/voice";
import { useApp } from "../lib/AppContext";
import { OfflineBanner } from "../components/OfflineBanner";

export default function TimeAllocation({
  job,
  crew,
  costCodes: initialCostCodes,
  vocabulary = [],
  pendingVoiceAllocation,
  onClearPendingVoice,
  onSaveAllocations,
  onBack,
  orgId,
}) {
  const { offlineQueue } = useApp();
  const { isOnline, isSyncing, pendingCount, syncAll } = offlineQueue;

  const [costCodes, setCostCodes] = useState(initialCostCodes || []);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showAddTime, setShowAddTime] = useState(false);
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(null);
  const [addTimeHours, setAddTimeHours] = useState("");
  const [addTimeCostCode, setAddTimeCostCode] = useState(null);
  const [pendingAllocations, setPendingAllocations] = useState([]);

  // Voice allocation state
  const [voiceResult, setVoiceResult] = useState(null);

  // Ref to track processed voice allocations
  const processedVoiceIds = useRef(new Set());

  // Cost code management state
  const [showManageCostCodes, setShowManageCostCodes] = useState(false);
  const [allOrgCostCodes, setAllOrgCostCodes] = useState([]);
  const [jobCostCodes, setJobCostCodes] = useState([]);
  const [loadingCostCodes, setLoadingCostCodes] = useState(false);
  const [newCostCode, setNewCostCode] = useState({
    code: "",
    name: "",
    unit: "EA",
  });
  const [showNewCostCodeForm, setShowNewCostCodeForm] = useState(false);
  const [savingCostCode, setSavingCostCode] = useState(false);

  // Update cost codes when initial changes
  useEffect(() => {
    setCostCodes(initialCostCodes || []);
  }, [initialCostCodes]);

  // Handle pending voice allocation from universal voice on Dashboard
  useEffect(() => {
    // Guard: wait for all required data
    if (!pendingVoiceAllocation) return;
    if (!costCodes || costCodes.length === 0) return;
    if (!crew || crew.length === 0) return;

    // Prevent double-processing in React Strict Mode
    const allocationId = pendingVoiceAllocation._id;
    if (allocationId && processedVoiceIds.current.has(allocationId)) {
      return;
    }
    if (allocationId) {
      processedVoiceIds.current.add(allocationId);
    }

    // Get the allocations from the universal format
    const timeAllocationData =
      pendingVoiceAllocation.extracted?.time_allocation ||
      pendingVoiceAllocation.extracted;

    if (!timeAllocationData) {
      console.warn("TimeAllocation: No time allocation data found");
      onClearPendingVoice?.();
      return;
    }

    const allocations = timeAllocationData?.allocations || [];
    if (allocations.length === 0) {
      console.warn("TimeAllocation: No allocations found");
      onClearPendingVoice?.();
      return;
    }

    // Expand "everyone" to individual crew members
    let expandedAllocations = [];
    allocations.forEach((alloc) => {
      if (
        /^(everyone|everybody|all|whole crew|the crew|crew|all crew|the whole crew)$/i.test(
          (alloc.employee_name || "").trim(),
        )
      ) {
        crew.forEach((member) => {
          expandedAllocations.push({ ...alloc, employee_name: member.name });
        });
      } else {
        expandedAllocations.push(alloc);
      }
    });

    // Transform and set result
    const transformed = {
      ...pendingVoiceAllocation,
      extracted: {
        ...timeAllocationData,
        allocations: expandedAllocations,
      },
    };

    // Only clear pending voice data if we successfully expanded allocations
    if (expandedAllocations.length > 0) {
      setVoiceResult(transformed);
      onClearPendingVoice?.();
    }
    // If expandedAllocations is empty, don't clear - let it retry when crew loads
    // Note: costCodes and crew in deps are guarded by processedVoiceIds ref to prevent double-fires
  }, [pendingVoiceAllocation, costCodes, crew, onClearPendingVoice]);

  // Load cost codes when modal opens
  useEffect(() => {
    if (showManageCostCodes && orgId) {
      loadCostCodeData();
    }
  }, [showManageCostCodes, orgId]); // loadCostCodeData is stable - defined in component scope

  const loadCostCodeData = async () => {
    setLoadingCostCodes(true);
    try {
      // Load all org cost codes
      const { data: allCodes, error: allError } = await supabase
        .from("t4s_cost_codes")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("code");

      if (allError) throw allError;
      setAllOrgCostCodes(allCodes || []);

      // Load job's current cost codes
      const { data: jobCodes, error: jobError } = await supabase
        .from("t4s_job_cost_codes")
        .select("*, cost_code:t4s_cost_codes(*)")
        .eq("job_id", job.rawId || job.id)
        .eq("is_active", true)
        .order("sort_order");

      if (jobError) throw jobError;
      setJobCostCodes(jobCodes || []);

      // Update the costCodes state used for allocation
      const activeCodes = (jobCodes || [])
        .map((jcc) => ({
          id: jcc.cost_code?.id,
          code: jcc.cost_code?.code,
          name: jcc.cost_code?.name,
          unit: jcc.cost_code?.unit,
        }))
        .filter((cc) => cc.id);
      setCostCodes(activeCodes);
    } catch (err) {
      console.error("Error loading cost codes:", err);
    } finally {
      setLoadingCostCodes(false);
    }
  };

  const handleCreateCostCode = async () => {
    if (!newCostCode.code || !newCostCode.name) return;
    setSavingCostCode(true);
    try {
      // Create the cost code
      const { data: createdCode, error: createError } = await supabase
        .from("t4s_cost_codes")
        .insert({
          org_id: orgId,
          code: newCostCode.code,
          name: newCostCode.name,
          unit: newCostCode.unit || "EA",
          is_active: true,
        })
        .select()
        .single();

      if (createError) {
        if (createError.code === "23505") {
          toast.info("A cost code with this code already exists");
        } else {
          throw createError;
        }
        return;
      }

      // Add it to the job
      const maxSort = jobCostCodes.reduce(
        (max, jcc) => Math.max(max, jcc.sort_order || 0),
        0,
      );
      await supabase.from("t4s_job_cost_codes").insert({
        job_id: job.rawId || job.id,
        cost_code_id: createdCode.id,
        source: "custom",
        sort_order: maxSort + 1,
      });

      // Refresh and reset
      await loadCostCodeData();
      setNewCostCode({ code: "", name: "", unit: "EA" });
      setShowNewCostCodeForm(false);
    } catch (err) {
      console.error("Error creating cost code:", err);
      toast.error("Error creating cost code");
    } finally {
      setSavingCostCode(false);
    }
  };

  const handleAddCostCodeToJob = async (costCode) => {
    try {
      const maxSort = jobCostCodes.reduce(
        (max, jcc) => Math.max(max, jcc.sort_order || 0),
        0,
      );
      const { error } = await supabase.from("t4s_job_cost_codes").insert({
        job_id: job.rawId || job.id,
        cost_code_id: costCode.id,
        source: "custom",
        sort_order: maxSort + 1,
      });

      if (error) {
        if (error.code === "23505") {
          toast.info("This cost code is already on the job");
        } else {
          throw error;
        }
      } else {
        await loadCostCodeData();
      }
    } catch (err) {
      console.error("Error adding cost code:", err);
    }
  };

  const handleRemoveCostCodeFromJob = async (jobCostCodeId) => {
    try {
      await supabase
        .from("t4s_job_cost_codes")
        .delete()
        .eq("id", jobCostCodeId);
      await loadCostCodeData();
    } catch (err) {
      console.error("Error removing cost code:", err);
    }
  };

  // Filter available cost codes (not already on job)
  const jobCostCodeIds = new Set(jobCostCodes.map((jcc) => jcc.cost_code_id));
  const availableCostCodes = allOrgCostCodes.filter(
    (cc) => !jobCostCodeIds.has(cc.id),
  );

  const getTotalAllocated = (employee) => {
    return employee.allocations.reduce((sum, a) => sum + a.hours, 0);
  };

  const getUnallocated = (employee) => {
    return (
      Math.round((employee.totalClocked - getTotalAllocated(employee)) * 10) /
      10
    );
  };

  const getPendingTotal = () => {
    return pendingAllocations.reduce((sum, a) => sum + a.hours, 0);
  };

  const getMaxAvailableHours = () => {
    if (selectedEmployees.length === 0) return 0;
    const selectedCrew = crew.filter((e) => selectedEmployees.includes(e.id));
    const pendingTotal = getPendingTotal();
    const availablePerEmployee = selectedCrew.map((emp) => {
      const alreadyAllocated = getTotalAllocated(emp);
      return emp.totalClocked - alreadyAllocated - pendingTotal;
    });
    return Math.max(0, Math.round(Math.min(...availablePerEmployee) * 10) / 10);
  };

  const toggleEmployee = (id) => {
    if (selectedEmployees.includes(id)) {
      setSelectedEmployees(selectedEmployees.filter((e) => e !== id));
    } else {
      setSelectedEmployees([...selectedEmployees, id]);
    }
  };

  const selectAll = () => {
    if (selectedEmployees.length === crew.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(crew.map((c) => c.id));
    }
  };

  const addToPending = () => {
    if (!addTimeCostCode || !addTimeHours) return;
    let hours = parseFloat(addTimeHours);
    const maxAvailable = getMaxAvailableHours();
    // Auto-cap to available hours instead of blocking
    if (hours > maxAvailable) {
      hours = maxAvailable;
    }
    if (hours <= 0) return;
    setPendingAllocations([
      ...pendingAllocations,
      {
        costCode: addTimeCostCode.code,
        costCodeId: addTimeCostCode.id,
        costName: addTimeCostCode.name,
        hours: hours,
      },
    ]);
    setAddTimeHours("");
    setAddTimeCostCode(null);
  };

  const fillRemaining = () => {
    if (!addTimeCostCode) return;
    const maxAvailable = getMaxAvailableHours();
    if (maxAvailable <= 0) return;
    setPendingAllocations([
      ...pendingAllocations,
      {
        costCode: addTimeCostCode.code,
        costCodeId: addTimeCostCode.id,
        costName: addTimeCostCode.name,
        hours: maxAvailable,
      },
    ]);
    setAddTimeHours("");
    setAddTimeCostCode(null);
  };

  const removeFromPending = (index) => {
    setPendingAllocations(pendingAllocations.filter((_, i) => i !== index));
  };

  const adjustPendingHours = (index, delta) => {
    setPendingAllocations(
      pendingAllocations.map((alloc, i) => {
        if (i === index) {
          const newHours = Math.round((alloc.hours + delta) * 10) / 10;
          if (newHours < 0.5) return alloc;
          if (delta > 0 && delta > getMaxAvailableHours()) return alloc;
          return { ...alloc, hours: newHours };
        }
        return alloc;
      }),
    );
  };

  // Guards against overlapping saves: a second save built from a stale crew
  // snapshot would silently replace (and lose) the first save's hours
  const saveInFlightRef = useRef(false);

  const saveAllPending = async () => {
    if (pendingAllocations.length === 0) return;
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;

    // Save allocations for each selected employee
    const savePromises = [];
    const failedEmployees = [];
    selectedEmployees.forEach((empId) => {
      const emp = crew.find((e) => e.id === empId);
      if (!emp) return;

      // Build new allocations list
      let newAllocations = [...(emp.allocations || [])];
      pendingAllocations.forEach((pending) => {
        const existingIndex = newAllocations.findIndex(
          (a) => a.costCode === pending.costCode,
        );
        if (existingIndex >= 0) {
          newAllocations[existingIndex] = {
            ...newAllocations[existingIndex],
            hours: newAllocations[existingIndex].hours + pending.hours,
          };
        } else {
          newAllocations.push({
            costCode: pending.costCode,
            costCodeId: pending.costCodeId,
            costName: pending.costName,
            hours: pending.hours,
          });
        }
      });

      // Validate: total allocation cannot exceed clocked time
      const totalAllocatedHours = newAllocations.reduce(
        (sum, a) => sum + (a.hours || 0),
        0,
      );
      const maxHours = emp.totalClocked || 0;

      if (totalAllocatedHours > maxHours && maxHours > 0) {
        // Scale down all allocations proportionally to fit within clocked time
        const scaleFactor = maxHours / totalAllocatedHours;
        newAllocations = newAllocations.map((a) => ({
          ...a,
          hours: Math.round(a.hours * scaleFactor * 10) / 10,
        }));
      }

      // Call the save callback for this employee
      if (onSaveAllocations) {
        const allocationsForApi = newAllocations.map((a) => ({
          costCodeId:
            a.costCodeId || costCodes.find((c) => c.code === a.costCode)?.id,
          hours: a.hours,
        }));
        savePromises.push(
          onSaveAllocations(empId, allocationsForApi).catch((err) => {
            console.error(`Failed to save for ${emp.name}:`, err);
            failedEmployees.push(emp.name);
          }),
        );
      }
    });

    // Wait for saves (each refreshes crew) BEFORE clearing the sheet, so a
    // follow-up allocation can't be built from a pre-save crew snapshot
    await Promise.all(savePromises);
    saveInFlightRef.current = false;

    setShowAddTime(false);
    setPendingAllocations([]);
    setAddTimeHours("");
    setAddTimeCostCode(null);
    setSelectedEmployees([]);

    if (failedEmployees.length > 0) {
      toast.error(
        `Failed to save allocations for: ${failedEmployees.join(", ")}`,
      );
    }
  };

  const cancelPending = () => {
    setShowAddTime(false);
    setPendingAllocations([]);
    setAddTimeHours("");
    setAddTimeCostCode(null);
  };

  const totalClocked = crew.reduce((sum, e) => sum + e.totalClocked, 0);
  const totalAllocated = crew.reduce((sum, e) => sum + getTotalAllocated(e), 0);
  const totalUnallocated =
    Math.round((totalClocked - totalAllocated) * 10) / 10;

  // Voice allocation handlers
  const handleVoiceResult = (result) => {
    setVoiceResult(result);
  };

  const handleVoiceError = (error) => {
    console.error("Voice error:", error);
    toast.error("Voice recording failed. Please try again.");
  };

  const handleVoiceConfirm = async (editedAllocations) => {
    if (!editedAllocations || editedAllocations.length === 0) {
      setVoiceResult(null);
      return;
    }

    // Group allocations by employee
    const allocationsByEmployee = {};
    const skippedAllocations = [];
    editedAllocations.forEach((alloc) => {
      if (!alloc.matchedEmployee) {
        console.warn("Skipping allocation - no matched employee:", alloc);
        skippedAllocations.push(alloc.employee_name);
        return;
      }

      const empId = alloc.matchedEmployee.id;
      if (!allocationsByEmployee[empId]) {
        allocationsByEmployee[empId] = {
          employee: alloc.matchedEmployee,
          allocations: [],
        };
      }

      allocationsByEmployee[empId].allocations.push({
        costCode: alloc.matchedCostCode?.code,
        costCodeId: alloc.matchedCostCode?.id,
        costName: alloc.matchedCostCode?.name,
        hours: alloc.hours,
        area: alloc.area,
        notes: alloc.notes,
      });
    });

    // Save allocations for each employee - collect all save promises
    const savePromises = [];
    const failedEmployees = [];
    const cappedEmployees = [];

    for (const { employee, allocations } of Object.values(
      allocationsByEmployee,
    )) {
      // Get existing allocations and merge with new ones
      let updatedAllocations = [...(employee.allocations || [])];

      allocations.forEach((newAlloc) => {
        const existingIndex = updatedAllocations.findIndex(
          (a) => a.costCode === newAlloc.costCode,
        );

        if (existingIndex >= 0) {
          // Update existing - add hours
          updatedAllocations[existingIndex] = {
            ...updatedAllocations[existingIndex],
            hours: updatedAllocations[existingIndex].hours + newAlloc.hours,
          };
        } else {
          updatedAllocations.push(newAlloc);
        }
      });

      // Validate: total allocation cannot exceed clocked time
      const totalAllocatedHours = updatedAllocations.reduce(
        (sum, a) => sum + (a.hours || 0),
        0,
      );
      const maxHours = employee.totalClocked || 0;

      if (totalAllocatedHours > maxHours && maxHours > 0) {
        // Scale down all allocations proportionally to fit within clocked time
        const scaleFactor = maxHours / totalAllocatedHours;
        updatedAllocations = updatedAllocations.map((a) => ({
          ...a,
          hours: Math.round(a.hours * scaleFactor * 10) / 10,
        }));
        cappedEmployees.push(
          `${employee.name} (capped to ${maxHours}h clocked)`,
        );
      }

      // Save via callback
      if (onSaveAllocations) {
        const allocationsForApi = updatedAllocations.map((a) => ({
          costCodeId:
            a.costCodeId || costCodes.find((c) => c.code === a.costCode)?.id,
          hours: a.hours,
          area: a.area,
          notes: a.notes,
        }));

        const savePromise = onSaveAllocations(employee.id, allocationsForApi)
          .then(() => {
            return { success: true, employee: employee.name };
          })
          .catch((err) => {
            console.error(`Failed to save for ${employee.name}:`, err);
            failedEmployees.push(employee.name);
            return { success: false, employee: employee.name, error: err };
          });

        savePromises.push(savePromise);
      } else {
        console.warn("No onSaveAllocations callback provided");
      }
    }

    // Wait for all saves to complete
    await Promise.all(savePromises);

    // Show alerts after all saves complete
    if (cappedEmployees.length > 0) {
      toast.warning(
        `Allocations were capped to clocked time for ${cappedEmployees.length} employees`,
      );
    }

    if (failedEmployees.length > 0) {
      toast.error(
        `Failed to save allocations for: ${failedEmployees.join(", ")}`,
      );
    }

    if (skippedAllocations.length > 0) {
      toast.warning(
        `Could not save allocations for ${skippedAllocations.length} entries (employee not found)`,
      );
    }

    setVoiceResult(null);
  };

  const handleVoiceCancel = () => {
    setVoiceResult(null);
  };

  // Group crew by role/trade for voice commands like "laborers did 4 hours cleanup"
  const crewByRole = {};
  crew.forEach((e) => {
    const role = (e.role || e.type || "crew").toLowerCase();
    if (!crewByRole[role]) crewByRole[role] = [];
    crewByRole[role].push(e.name);
  });

  // Build screen context for voice processing - include clocked hours per employee
  const voiceScreenContext = {
    crew_roster: crew.map(
      (e) =>
        `${e.name} (${e.role || e.type || "crew"}, ${e.totalClocked}h clocked)`,
    ),
    crew_by_role: Object.entries(crewByRole).map(
      ([role, names]) => `${role}: ${names.join(", ")}`,
    ),
    crew_hours: crew.map((e) => ({
      name: e.name,
      role: e.role || e.type || "crew",
      clocked: e.totalClocked,
    })),
    job_name: `${job.id} - ${job.name}`,
    job_cost_codes: costCodes.map((cc) => `${cc.code} - ${cc.name}`),
    total_clocked: totalClocked,
    date: new Date().toLocaleDateString("en-CA"),
    vocabulary: vocabulary,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        paddingBottom: selectedEmployees.length > 0 ? "100px" : "0",
      }}
    >
      {/* Offline Banner */}
      <OfflineBanner
        isOnline={isOnline}
        isSyncing={isSyncing}
        pendingCount={pendingCount}
        onSyncNow={syncAll}
      />

      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onBack}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "-8px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
            Time Allocation
          </h1>
        </div>
      </div>

      {/* Job Bar */}
      <div
        style={{
          background: "rgba(26,90,142,0.05)",
          padding: "12px 16px",
          borderBottom: "1px solid rgba(26,90,142,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "14px", color: "#1a5a8e", fontWeight: "500" }}>
          {job.id} • {job.name}
        </div>
        <div style={{ fontSize: "13px", color: "#64748b" }}>
          {new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      </div>

      {/* Summary Bar */}
      <div
        style={{
          padding: "12px 16px",
          background: "white",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}
          >
            {totalClocked}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Clocked
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{ fontSize: "20px", fontWeight: "700", color: "#10b981" }}
          >
            {totalAllocated}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Allocated
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: totalUnallocated > 0 ? "#f59e0b" : "#10b981",
            }}
          >
            {totalUnallocated}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Remaining
          </div>
        </div>
      </div>

      {/* Voice Allocate Section */}
      <div
        style={{
          padding: "12px 16px",
          background: "white",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <VoiceButton
          contextType="time_allocation"
          jobId={job.rawId || job.id}
          screenContext={voiceScreenContext}
          onResult={handleVoiceResult}
          onError={handleVoiceError}
          style={{
            width: "100%",
            padding: "16px",
            borderRadius: "12px",
            fontSize: "16px",
          }}
        />
        <div
          style={{
            fontSize: "12px",
            color: "#64748b",
            textAlign: "center",
            marginTop: "8px",
          }}
        >
          "Jose 8 hours block, Tommy split 4 north 4 south, Mike left early 6
          hours"
        </div>
      </div>

      {/* Select All Bar */}
      <div
        style={{
          padding: "8px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button
          onClick={selectAll}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
            color: "#1a5a8e",
            fontWeight: "500",
          }}
        >
          <div
            style={{
              width: "22px",
              height: "22px",
              borderRadius: "6px",
              border:
                selectedEmployees.length === crew.length
                  ? "none"
                  : "2px solid #cbd5e1",
              background:
                selectedEmployees.length === crew.length ? "#1a5a8e" : "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {selectedEmployees.length === crew.length && (
              <svg
                width="14"
                height="14"
                fill="none"
                stroke="white"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
          Select All ({crew.length})
        </button>
        {selectedEmployees.length > 0 && (
          <span
            style={{
              fontSize: "13px",
              color: "white",
              background: "#1a5a8e",
              padding: "4px 12px",
              borderRadius: "999px",
              fontWeight: "600",
            }}
          >
            {selectedEmployees.length} selected
          </span>
        )}
      </div>

      {/* Employee List */}
      <div>
        {crew.map((employee) => {
          const unallocated = getUnallocated(employee);
          const isSelected = selectedEmployees.includes(employee.id);

          return (
            <div
              key={employee.id}
              style={{ background: "white", borderBottom: "1px solid #f1f5f9" }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  background: isSelected ? "rgba(26,90,142,0.03)" : "white",
                }}
              >
                <button
                  onClick={() => toggleEmployee(employee.id)}
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    border: isSelected ? "none" : "2px solid #cbd5e1",
                    background: isSelected ? "#1a5a8e" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  {isSelected && (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="white"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>

                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    background: employee.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "600",
                    fontSize: "15px",
                    flexShrink: 0,
                  }}
                >
                  {employee.name.split(",")[0].slice(0, 2).toUpperCase()}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    {employee.name}
                  </div>
                  <div style={{ fontSize: "13px", color: "#64748b" }}>
                    {employee.clockIn} - {employee.clockOut || "In"} •{" "}
                    {employee.totalClocked}h
                  </div>
                </div>

                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {unallocated > 0 ? (
                    <div
                      style={{
                        background: "#fef3c7",
                        color: "#92400e",
                        padding: "4px 10px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {unallocated}h left
                    </div>
                  ) : unallocated < 0 ? (
                    <div
                      style={{
                        background: "#fee2e2",
                        color: "#dc2626",
                        padding: "4px 10px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {Math.abs(unallocated)}h over
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#dcfce7",
                        color: "#166534",
                        padding: "4px 10px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      Done
                    </div>
                  )}
                </div>

                <button
                  onClick={() =>
                    setShowEmployeeDetail(
                      showEmployeeDetail === employee.id ? null : employee.id,
                    )
                  }
                  style={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    transform:
                      showEmployeeDetail === employee.id
                        ? "rotate(90deg)"
                        : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="#94a3b8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              {showEmployeeDetail === employee.id && (
                <div style={{ padding: "0 16px 12px 98px" }}>
                  {employee.allocations.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {employee.allocations.map((alloc, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                          }}
                        >
                          <span style={{ fontSize: "14px", color: "#475569" }}>
                            {alloc.costCode} - {alloc.costName}
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                            }}
                          >
                            {alloc.hours}h
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#94a3b8",
                        fontStyle: "italic",
                        padding: "8px 0",
                      }}
                    >
                      No time allocated yet
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setSelectedEmployees([employee.id]);
                      setShowAddTime(true);
                    }}
                    style={{
                      marginTop: "8px",
                      padding: "8px 12px",
                      background: "white",
                      border: "1px dashed #cbd5e1",
                      borderRadius: "8px",
                      color: "#1a5a8e",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    + Add Time
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Time Modal */}
      {showAddTime && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={cancelPending}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: "12px 24px 0" }}>
              <div
                style={{
                  width: "40px",
                  height: "4px",
                  background: "#cbd5e1",
                  borderRadius: "2px",
                  margin: "0 auto",
                }}
              />
            </div>

            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    Add Time
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      marginTop: "2px",
                    }}
                  >
                    {selectedEmployees.length} employee
                    {selectedEmployees.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    background:
                      getMaxAvailableHours() > 0 ? "#f0fdf4" : "#fef2f2",
                    padding: "6px 12px",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getMaxAvailableHours() > 0 ? "#10b981" : "#ef4444",
                    }}
                  >
                    {getMaxAvailableHours()}h
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>
                    available
                  </div>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
              {pendingAllocations.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      marginBottom: "8px",
                    }}
                  >
                    Adding ({getPendingTotal()}h total)
                  </div>
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: "12px",
                      padding: "8px",
                    }}
                  >
                    {pendingAllocations.map((alloc, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          background: "white",
                          borderRadius: "8px",
                          marginBottom:
                            i < pendingAllocations.length - 1 ? "6px" : 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: "15px",
                            color: "#1e293b",
                            fontWeight: "500",
                          }}
                        >
                          {alloc.costCode} - {alloc.costName}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <button
                            onClick={() => adjustPendingHours(i, -0.5)}
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: "#f1f5f9",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "18px",
                              color: "#64748b",
                            }}
                          >
                            −
                          </button>
                          <span
                            style={{
                              fontSize: "16px",
                              fontWeight: "700",
                              color: "#10b981",
                              minWidth: "36px",
                              textAlign: "center",
                            }}
                          >
                            {alloc.hours}h
                          </span>
                          <button
                            onClick={() => adjustPendingHours(i, 0.5)}
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: "#f1f5f9",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "18px",
                              color: "#64748b",
                            }}
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromPending(i)}
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: "#fee2e2",
                              border: "none",
                              cursor: "pointer",
                              marginLeft: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              fill="none"
                              stroke="#dc2626"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Hours
                </label>
                <div
                  style={{ display: "flex", gap: "6px", marginBottom: "8px" }}
                >
                  {[0.5, 1, 2, 4, 6, 8].map((h) => (
                    <button
                      key={h}
                      onClick={() => setAddTimeHours(h.toString())}
                      style={{
                        flex: 1,
                        padding: "14px 4px",
                        borderRadius: "10px",
                        border:
                          addTimeHours === h.toString()
                            ? "2px solid #1a5a8e"
                            : "1px solid #e2e8f0",
                        background:
                          addTimeHours === h.toString() ? "#e8f4fc" : "white",
                        fontSize: "16px",
                        fontWeight: "600",
                        color:
                          addTimeHours === h.toString() ? "#1a5a8e" : "#1e293b",
                        cursor: "pointer",
                      }}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  step="0.5"
                  placeholder="Custom hours..."
                  value={addTimeHours}
                  onChange={(e) => setAddTimeHours(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    fontSize: "16px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Fill Remaining Button */}
              {getMaxAvailableHours() > 0 && addTimeCostCode && (
                <button
                  onClick={fillRemaining}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: "2px dashed #10b981",
                    background: "#f0fdf4",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#10b981",
                    cursor: "pointer",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Fill {getMaxAvailableHours()}h Remaining
                </button>
              )}

              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Cost Code
                  </label>
                  <button
                    onClick={() => setShowManageCostCodes(true)}
                    style={{
                      fontSize: "13px",
                      color: "#8b5cf6",
                      fontWeight: "600",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    + Manage Codes
                  </button>
                </div>
                {costCodes.length === 0 ? (
                  <div
                    style={{
                      padding: "24px",
                      background: "#f8fafc",
                      borderRadius: "12px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        marginBottom: "12px",
                      }}
                    >
                      No cost codes on this job yet
                    </div>
                    <button
                      onClick={() => setShowManageCostCodes(true)}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        background: "#8b5cf6",
                        color: "white",
                        border: "none",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      + Add Cost Codes
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                    }}
                  >
                    {costCodes.map((cc) => (
                      <button
                        key={cc.code}
                        onClick={() => setAddTimeCostCode(cc)}
                        style={{
                          padding: "12px",
                          borderRadius: "10px",
                          border:
                            addTimeCostCode?.code === cc.code
                              ? "2px solid #1a5a8e"
                              : "1px solid #e2e8f0",
                          background:
                            addTimeCostCode?.code === cc.code
                              ? "#e8f4fc"
                              : "white",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color:
                              addTimeCostCode?.code === cc.code
                                ? "#1a5a8e"
                                : "#1e293b",
                          }}
                        >
                          {cc.code}
                        </div>
                        <div style={{ fontSize: "13px", color: "#64748b" }}>
                          {cc.name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: "16px 24px",
                paddingBottom: "28px",
                borderTop: "1px solid #e2e8f0",
                background: "white",
              }}
            >
              <button
                onClick={addToPending}
                disabled={!addTimeHours || !addTimeCostCode}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "none",
                  background:
                    !addTimeHours || !addTimeCostCode ? "#e2e8f0" : "#10b981",
                  fontSize: "16px",
                  fontWeight: "600",
                  color:
                    !addTimeHours || !addTimeCostCode ? "#94a3b8" : "white",
                  cursor:
                    !addTimeHours || !addTimeCostCode
                      ? "not-allowed"
                      : "pointer",
                  marginBottom: "10px",
                }}
              >
                {addTimeHours && addTimeCostCode
                  ? `+ Add ${addTimeHours}h ${addTimeCostCode.name}`
                  : "Select hours & cost code"}
              </button>
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={cancelPending}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    background: "white",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#64748b",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveAllPending}
                  disabled={pendingAllocations.length === 0}
                  style={{
                    flex: 2,
                    padding: "14px",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      pendingAllocations.length === 0 ? "#e2e8f0" : "#1a5a8e",
                    fontSize: "15px",
                    fontWeight: "600",
                    color:
                      pendingAllocations.length === 0 ? "#94a3b8" : "white",
                    cursor:
                      pendingAllocations.length === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {pendingAllocations.length === 0
                    ? "Save & Close"
                    : `Save ${getPendingTotal()}h to ${selectedEmployees.length} people`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Action Bar */}
      {selectedEmployees.length > 0 && !showAddTime && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "white",
            borderTop: "1px solid #e2e8f0",
            padding: "12px 16px",
            paddingBottom: "28px",
            display: "flex",
            gap: "12px",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
            maxWidth: "430px",
            margin: "0 auto",
          }}
        >
          <button
            onClick={() => setSelectedEmployees([])}
            style={{
              padding: "14px 16px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              background: "white",
              fontSize: "15px",
              fontWeight: "600",
              color: "#64748b",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
          <button
            onClick={() => setShowAddTime(true)}
            style={{
              flex: 1,
              padding: "14px 20px",
              borderRadius: "12px",
              border: "none",
              background: "#1a5a8e",
              fontSize: "15px",
              fontWeight: "600",
              color: "white",
              cursor: "pointer",
            }}
          >
            + Add Time to {selectedEmployees.length}
          </button>
        </div>
      )}

      {/* Manage Cost Codes Modal */}
      {showManageCostCodes && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => {
              setShowManageCostCodes(false);
              setShowNewCostCodeForm(false);
            }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              padding: "24px",
              paddingBottom: "40px",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: "#cbd5e1",
                borderRadius: "2px",
                margin: "0 auto 16px",
              }}
            />

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Manage Cost Codes
              </div>
              <div
                style={{ fontSize: "14px", color: "#64748b", marginTop: "4px" }}
              >
                Add or create cost codes for {job.id}
              </div>
            </div>

            {loadingCostCodes ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#64748b",
                }}
              >
                Loading...
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: "auto" }}>
                {/* Create New Cost Code */}
                {showNewCostCodeForm ? (
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: "12px",
                      padding: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#166534",
                        marginBottom: "12px",
                      }}
                    >
                      Create New Cost Code
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Code (e.g. 20)"
                        value={newCostCode.code}
                        onChange={(e) =>
                          setNewCostCode({
                            ...newCostCode,
                            code: e.target.value,
                          })
                        }
                        style={{
                          width: "80px",
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          fontSize: "14px",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Name (e.g. Brick)"
                        value={newCostCode.name}
                        onChange={(e) =>
                          setNewCostCode({
                            ...newCostCode,
                            name: e.target.value,
                          })
                        }
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: "12px" }}>
                      <select
                        value={newCostCode.unit}
                        onChange={(e) =>
                          setNewCostCode({
                            ...newCostCode,
                            unit: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          fontSize: "14px",
                          background: "white",
                        }}
                      >
                        <option value="EA">EA - Each</option>
                        <option value="SF">SF - Square Feet</option>
                        <option value="LF">LF - Linear Feet</option>
                        <option value="HR">HR - Hours</option>
                        <option value="CF">CF - Cubic Feet</option>
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => {
                          setShowNewCostCodeForm(false);
                          setNewCostCode({ code: "", name: "", unit: "EA" });
                        }}
                        style={{
                          flex: 1,
                          padding: "10px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          background: "white",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#64748b",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateCostCode}
                        disabled={
                          !newCostCode.code ||
                          !newCostCode.name ||
                          savingCostCode
                        }
                        style={{
                          flex: 2,
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          background:
                            !newCostCode.code || !newCostCode.name
                              ? "#e2e8f0"
                              : "#10b981",
                          fontSize: "14px",
                          fontWeight: "600",
                          color:
                            !newCostCode.code || !newCostCode.name
                              ? "#94a3b8"
                              : "white",
                          cursor:
                            !newCostCode.code || !newCostCode.name
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {savingCostCode ? "Creating..." : "Create & Add to Job"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewCostCodeForm(true)}
                    style={{
                      width: "100%",
                      padding: "14px",
                      borderRadius: "12px",
                      border: "2px dashed #10b981",
                      background: "#f0fdf4",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#10b981",
                      cursor: "pointer",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "18px" }}>+</span> Create New Cost
                    Code
                  </button>
                )}

                {/* Current Job Cost Codes */}
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      marginBottom: "8px",
                    }}
                  >
                    On This Job ({jobCostCodes.length})
                  </div>
                  {jobCostCodes.length === 0 ? (
                    <div
                      style={{
                        padding: "16px",
                        background: "#f8fafc",
                        borderRadius: "8px",
                        color: "#94a3b8",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    >
                      No cost codes assigned yet
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    >
                      {jobCostCodes.map((jcc, index) => (
                        <div
                          key={jcc.id}
                          style={{
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom:
                              index < jobCostCodes.length - 1
                                ? "1px solid #e2e8f0"
                                : "none",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#334155",
                              }}
                            >
                              {jcc.cost_code?.code} - {jcc.cost_code?.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                              {jcc.cost_code?.unit}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveCostCodeFromJob(jcc.id)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Cost Codes */}
                {availableCostCodes.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        marginBottom: "8px",
                      }}
                    >
                      Available to Add ({availableCostCodes.length})
                    </div>
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        overflow: "hidden",
                      }}
                    >
                      {availableCostCodes.map((cc, index) => (
                        <button
                          key={cc.id}
                          onClick={() => handleAddCostCodeToJob(cc)}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: "transparent",
                            border: "none",
                            borderBottom:
                              index < availableCostCodes.length - 1
                                ? "1px solid #f1f5f9"
                                : "none",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "500",
                                color: "#334155",
                              }}
                            >
                              {cc.code} - {cc.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                              {cc.unit}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "#8b5cf6",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}
                          >
                            Add
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => {
                setShowManageCostCodes(false);
                setShowNewCostCodeForm(false);
              }}
              style={{
                marginTop: "16px",
                padding: "14px",
                borderRadius: "12px",
                background: "#1a5a8e",
                border: "none",
                fontSize: "15px",
                fontWeight: "600",
                color: "white",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Voice Allocation Preview Modal */}
      {voiceResult && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={handleVoiceCancel}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <TimeAllocationPreview
              result={voiceResult}
              crew={crew}
              costCodes={costCodes}
              orgId={orgId}
              onConfirm={handleVoiceConfirm}
              onCancel={handleVoiceCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * TimeAllocationPreview - Shows extracted time allocations for confirmation
 */
function TimeAllocationPreview({
  result,
  crew,
  costCodes,
  orgId,
  onConfirm,
  onCancel,
}) {
  const [editedAllocations, setEditedAllocations] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [learnModal, setLearnModal] = useState(null); // { term, type: 'nickname'|'activity', allocationIndex }

  const { transcript, extracted, confidence, needs_review } = result || {};
  const issues = extracted?.issues || [];
  const confidencePercent = Math.round((confidence || 0) * 100);

  // Initialize edited allocations from extracted data
  useEffect(() => {
    // Handle both direct allocations and nested time_allocation structure
    const allocations =
      extracted?.allocations || extracted?.time_allocation?.allocations;

    if (!allocations || allocations.length === 0) {
      return;
    }

    // Helper to match cost code
    const findCostCode = (hint, activity) => {
      let matchedCostCode = null;
      if (hint) {
        matchedCostCode = costCodes.find(
          (cc) =>
            cc.name.toLowerCase().includes(hint.toLowerCase()) ||
            cc.code.toLowerCase().includes(hint.toLowerCase()) ||
            hint.toLowerCase().includes(cc.name.toLowerCase()) ||
            hint.toLowerCase().includes(cc.code.toLowerCase()),
        );
      }
      if (!matchedCostCode && activity) {
        matchedCostCode = costCodes.find(
          (cc) =>
            cc.name.toLowerCase().includes(activity.toLowerCase()) ||
            activity.toLowerCase().includes(cc.name.toLowerCase()),
        );
      }
      if (!matchedCostCode && costCodes.length > 0) {
        matchedCostCode = costCodes[0];
      }
      return matchedCostCode;
    };

    // Check if any allocation is for "everyone" / "all" / "whole crew"
    const everyoneAllocations = allocations.filter((a) =>
      /^(everyone|everybody|all|whole crew|the crew|crew|all crew|the whole crew)$/i.test(
        (a.employee_name || "").trim(),
      ),
    );

    let allocationsToProcess = allocations;

    // If there are "everyone" allocations, expand them to all crew members
    if (everyoneAllocations.length > 0) {
      allocationsToProcess = [];
      allocations.forEach((alloc) => {
        if (
          /^(everyone|everybody|all|whole crew|the crew|crew|all crew|the whole crew)$/i.test(
            (alloc.employee_name || "").trim(),
          )
        ) {
          // Expand to all crew members
          crew.forEach((member) => {
            allocationsToProcess.push({
              ...alloc,
              employee_name: member.name,
            });
          });
        } else {
          allocationsToProcess.push(alloc);
        }
      });
    }

    const matched = allocationsToProcess.map((alloc) => {
      // Find matching employee
      const employee = crew.find((e) => {
        const name = e.name.toLowerCase();
        const allocName = (alloc.employee_name || "").toLowerCase();
        return (
          name.includes(allocName) ||
          allocName.includes(name.split(",")[0].trim().toLowerCase()) ||
          allocName.includes(name.split(" ")[0].toLowerCase())
        );
      });

      // Find matching cost code
      const matchedCostCode = findCostCode(
        alloc.cost_code_hint,
        alloc.activity,
      );

      return {
        ...alloc,
        matchedEmployee: employee,
        matchedCostCode,
        originalHours: alloc.hours, // Track original hours to detect edits
        hasWarning:
          !employee ||
          alloc.notes ||
          (employee && alloc.hours > employee.totalClocked),
      };
    });

    setEditedAllocations(matched);
  }, [extracted, crew, costCodes]);

  if (!result) return null;

  const totalHours = editedAllocations.reduce(
    (sum, a) => sum + (a.hours || 0),
    0,
  );

  // Calculate max available hours for an allocation (considering other allocations for same employee)
  const getMaxHoursForAllocation = (index) => {
    const alloc = editedAllocations[index];
    if (!alloc?.matchedEmployee) return 99; // No limit if no matched employee

    const employee = alloc.matchedEmployee;
    const maxHours = employee.totalClocked || 0;

    // Calculate total hours for this employee across other allocations
    const otherAllocationsForEmployee = editedAllocations.filter(
      (a, i) => i !== index && a.matchedEmployee?.id === employee.id,
    );
    const otherHours = otherAllocationsForEmployee.reduce(
      (sum, a) => sum + (a.hours || 0),
      0,
    );

    return Math.max(0, Math.round((maxHours - otherHours) * 10) / 10);
  };

  const updateAllocation = (index, field, value) => {
    setEditedAllocations((prev) => {
      const updated = [...prev];
      let newValue = value;

      // If updating hours, validate against clocked time
      if (field === "hours" && updated[index].matchedEmployee) {
        const employee = updated[index].matchedEmployee;
        const maxHours = employee.totalClocked || 0;

        // Calculate total hours for this employee across all allocations
        const otherAllocationsForEmployee = updated.filter(
          (a, i) => i !== index && a.matchedEmployee?.id === employee.id,
        );
        const otherHours = otherAllocationsForEmployee.reduce(
          (sum, a) => sum + (a.hours || 0),
          0,
        );

        // Cap the new value so total doesn't exceed clocked time
        const availableHours = Math.max(0, maxHours - otherHours);
        if (newValue > availableHours) {
          newValue = Math.round(availableHours * 10) / 10;
        }
      }

      updated[index] = { ...updated[index], [field]: newValue };
      return updated;
    });
  };

  const removeAllocation = (index) => {
    setEditedAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    // Pass edited allocations back
    onConfirm(editedAllocations);
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Time Allocation
        </h3>
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600",
            background: needs_review ? "#fef3c7" : "#dcfce7",
            color: needs_review ? "#92400e" : "#166534",
          }}
        >
          {needs_review ? "Needs Review" : "Auto-verified"}
        </span>
      </div>

      <div style={{ padding: "16px", maxHeight: "60vh", overflowY: "auto" }}>
        {/* Transcript */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            What you said
          </label>
          <p
            style={{
              margin: 0,
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              color: "#475569",
              fontStyle: "italic",
            }}
          >
            "{transcript || "No transcript available"}"
          </p>
        </div>

        {/* Allocations - Editable */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            Allocations ({totalHours}h total) - Tap to edit
          </label>
          <div
            style={{
              background: "#f0f9ff",
              borderRadius: "10px",
              border: "1px solid #bae6fd",
              overflow: "hidden",
            }}
          >
            {editedAllocations.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}
              >
                No allocations extracted
              </div>
            ) : (
              editedAllocations.map((alloc, i) => (
                <div
                  key={i}
                  style={{
                    borderBottom:
                      i < editedAllocations.length - 1
                        ? "1px solid #bae6fd"
                        : "none",
                  }}
                >
                  {editingIndex === i ? (
                    // Edit mode
                    <div
                      style={{
                        padding: "12px",
                        background: "#e0f2fe",
                      }}
                    >
                      <div style={{ marginBottom: "10px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Employee
                        </label>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          {alloc.matchedEmployee?.name || alloc.employee_name}
                          {alloc.matchedEmployee && (
                            <span
                              style={{
                                fontWeight: "400",
                                color: "#64748b",
                                marginLeft: "8px",
                              }}
                            >
                              ({alloc.matchedEmployee.totalClocked}h clocked)
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            Hours (max{" "}
                            {getMaxHoursForAllocation(i) + alloc.hours}h)
                          </label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max={getMaxHoursForAllocation(i) + alloc.hours}
                            value={alloc.hours}
                            onChange={(e) =>
                              updateAllocation(
                                i,
                                "hours",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              border: "1px solid #cbd5e1",
                              fontSize: "16px",
                              fontWeight: "600",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div style={{ flex: 2 }}>
                          <label
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            Cost Code
                          </label>
                          <select
                            value={alloc.matchedCostCode?.id || ""}
                            onChange={(e) => {
                              const cc = costCodes.find(
                                (c) => c.id === e.target.value,
                              );
                              updateAllocation(i, "matchedCostCode", cc);
                            }}
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              border: "1px solid #cbd5e1",
                              fontSize: "14px",
                              background: "white",
                              boxSizing: "border-box",
                            }}
                          >
                            {costCodes.map((cc) => (
                              <option key={cc.id} value={cc.id}>
                                {cc.code} - {cc.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Area (optional)
                        </label>
                        <input
                          type="text"
                          value={alloc.area || ""}
                          onChange={(e) =>
                            updateAllocation(i, "area", e.target.value)
                          }
                          placeholder="e.g., North wall, Section A"
                          style={{
                            width: "100%",
                            padding: "10px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontSize: "14px",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => removeAllocation(i)}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid #fca5a5",
                            background: "#fee2e2",
                            color: "#dc2626",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => setEditingIndex(null)}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "#1a5a8e",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode - tap to edit
                    <div
                      onClick={() => setEditingIndex(i)}
                      style={{
                        padding: "12px",
                        background: alloc.hasWarning
                          ? "#fffbeb"
                          : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "4px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: "600",
                              color: alloc.matchedEmployee
                                ? "#1e293b"
                                : "#dc2626",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            {alloc.matchedEmployee
                              ? alloc.matchedEmployee.name
                              : alloc.employee_name}
                            {!alloc.matchedEmployee && (
                              <>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    background: "#fee2e2",
                                    color: "#dc2626",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  Unknown
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLearnModal({
                                      term: alloc.employee_name,
                                      type: "nickname",
                                      allocationIndex: i,
                                    });
                                  }}
                                  style={{
                                    fontSize: "11px",
                                    background: "#dbeafe",
                                    color: "#1d4ed8",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                  }}
                                >
                                  Teach
                                </button>
                              </>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#64748b",
                              marginTop: "2px",
                            }}
                          >
                            {alloc.matchedCostCode?.name ||
                              alloc.activity ||
                              "Unassigned"}
                            {alloc.area && ` • ${alloc.area}`}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {alloc.hours !== alloc.originalHours && (
                            <span
                              title={`Originally ${alloc.originalHours}h`}
                              style={{
                                fontSize: "11px",
                                background: "#fef3c7",
                                color: "#92400e",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontWeight: "600",
                                cursor: "help",
                              }}
                            >
                              Edited
                            </span>
                          )}
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "700",
                              color:
                                alloc.hours !== alloc.originalHours
                                  ? "#92400e"
                                  : "#1a5a8e",
                            }}
                          >
                            {alloc.hours}h
                          </div>
                          <span style={{ color: "#94a3b8", fontSize: "14px" }}>
                            ✏️
                          </span>
                        </div>
                      </div>
                      {alloc.matchedEmployee &&
                        alloc.hours > alloc.matchedEmployee.totalClocked && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#dc2626",
                              background: "#fee2e2",
                              padding: "6px 8px",
                              borderRadius: "6px",
                              marginTop: "6px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>⚠️</span> Exceeds clocked time (
                            {alloc.matchedEmployee.totalClocked}h)
                          </div>
                        )}
                      {alloc.notes && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#ea580c",
                            background: "#fff7ed",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            marginTop: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>⚠️</span> {alloc.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Issues */}
        {issues.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Issues Noted
            </label>
            <div
              style={{
                background: "#fef3c7",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #fcd34d",
              }}
            >
              {issues.map((issue, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: "14px",
                    color: "#92400e",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: i < issues.length - 1 ? "6px" : 0,
                  }}
                >
                  <span>⚠️</span> {issue}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confidence indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Confidence
          </span>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: 1,
                background: "#e2e8f0",
                borderRadius: "4px",
                height: "8px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: "4px",
                  background:
                    confidence > 0.8
                      ? "#22c55e"
                      : confidence > 0.5
                        ? "#eab308"
                        : "#ef4444",
                  width: `${confidencePercent}%`,
                  transition: "width 0.3s",
                }}
              />
            </div>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#475569",
                minWidth: "40px",
              }}
            >
              {confidencePercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "16px",
          borderTop: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            background: "white",
            fontSize: "14px",
            fontWeight: "600",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        <button
          onClick={handleConfirm}
          disabled={editedAllocations.length === 0}
          style={{
            flex: 2,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "none",
            background: editedAllocations.length === 0 ? "#cbd5e1" : "#1a5a8e",
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            cursor: editedAllocations.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Confirm All ({totalHours}h)
        </button>
      </div>

      {/* Learn Term Modal */}
      {learnModal && (
        <LearnTermModal
          term={learnModal.term}
          type={learnModal.type}
          crew={crew}
          costCodes={costCodes}
          orgId={orgId}
          onSave={async (selectedEmployee) => {
            // Add to vocabulary
            try {
              await addVocabularyTerm({
                orgId,
                term: learnModal.term,
                termType: "nickname",
                resolvesTo: selectedEmployee.name,
                employeeId: selectedEmployee.id,
              });
              // Update the allocation with the matched employee
              updateAllocation(
                learnModal.allocationIndex,
                "matchedEmployee",
                selectedEmployee,
              );
              updateAllocation(learnModal.allocationIndex, "hasWarning", false);
              setLearnModal(null);
            } catch (err) {
              console.error("Failed to save vocabulary term:", err);
              toast.error("Failed to save. Please try again.");
            }
          }}
          onCancel={() => setLearnModal(null)}
        />
      )}
    </div>
  );
}

/**
 * LearnTermModal - Popup to teach the system a new term
 */
function LearnTermModal({
  term,
  type,
  crew,
  costCodes,
  orgId,
  onSave,
  onCancel,
}) {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedEmployee) return;
    setSaving(true);
    try {
      await onSave(selectedEmployee);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
        }}
        onClick={onCancel}
      />
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "340px",
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid #e2e8f0",
            background: "#f0f9ff",
          }}
        >
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "4px",
            }}
          >
            Teach New Term
          </div>
          <div style={{ fontSize: "14px", color: "#64748b" }}>
            Who is "
            <span style={{ fontWeight: "600", color: "#1d4ed8" }}>{term}</span>
            "?
          </div>
        </div>

        {/* Employee List */}
        <div style={{ maxHeight: "300px", overflowY: "auto", padding: "8px" }}>
          {crew.map((emp) => (
            <button
              key={emp.id}
              onClick={() => setSelectedEmployee(emp)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px",
                borderRadius: "10px",
                border:
                  selectedEmployee?.id === emp.id
                    ? "2px solid #1d4ed8"
                    : "1px solid transparent",
                background:
                  selectedEmployee?.id === emp.id ? "#eff6ff" : "transparent",
                cursor: "pointer",
                textAlign: "left",
                marginBottom: "4px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: emp.color || "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "13px",
                  flexShrink: 0,
                }}
              >
                {emp.name.split(",")[0].slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#1e293b",
                  }}
                >
                  {emp.name}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>
                  {emp.type || "Crew"}
                </div>
              </div>
              {selectedEmployee?.id === emp.id && (
                <svg width="20" height="20" fill="#1d4ed8" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            padding: "16px",
            borderTop: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              background: "white",
              fontSize: "14px",
              fontWeight: "600",
              color: "#64748b",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedEmployee || saving}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "10px",
              border: "none",
              background: !selectedEmployee ? "#cbd5e1" : "#1d4ed8",
              fontSize: "14px",
              fontWeight: "600",
              color: "white",
              cursor: !selectedEmployee ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
